import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { Fleet } from "../../types/auth";
import { BikeMultiSelect } from "../common/BikeMultiSelect";
import { apiService } from "../../services/api";

interface FleetModalProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  fleet?: Fleet | null;
  onSaved: () => void;
}

export const FleetModal: React.FC<FleetModalProps> = ({
  open,
  onClose,
  mode,
  fleet,
  onSaved,
}) => {
  const [name, setName] = useState("");
  const [bikeIds, setBikeIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (mode === "create") {
      setName("");
      setBikeIds([]);
      return;
    }
    if (fleet) {
      setName(fleet.name);
      setLoadingDetail(true);
      apiService
        .getFleetById(fleet.id)
        .then((detail) => {
          setBikeIds(detail.bike_ids ?? []);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : "Failed to load fleet");
        })
        .finally(() => setLoadingDetail(false));
    }
  }, [open, mode, fleet]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Fleet name is required");
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (mode === "create") {
        await apiService.createFleet({ name: name.trim(), bike_ids: bikeIds });
      } else if (fleet) {
        await apiService.updateFleet(fleet.id, {
          name: name.trim(),
          bike_ids: bikeIds,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save fleet");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        />
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {mode === "create" ? "Add Fleet" : "Edit Fleet"}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {error && (
                <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </p>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fleet name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="e.g. Acme Mobility"
                    disabled={loading || loadingDetail}
                  />
                </div>

                {loadingDetail ? (
                  <p className="text-sm text-gray-500">Loading vehicles...</p>
                ) : (
                  <BikeMultiSelect
                    label="Vehicles in this fleet"
                    selectedBikeIds={bikeIds}
                    onChange={setBikeIds}
                    disabled={loading}
                  />
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
              <button
                type="submit"
                disabled={loading || loadingDetail}
                className="w-full sm:w-auto inline-flex justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading
                  ? "Saving..."
                  : mode === "create"
                    ? "Create Fleet"
                    : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
